import HydrologyScatterPlot from '../../plots/HydrologyScatterPlot';
import GaugeDial from '../../plots/GaugeDial';
import { useAppStateContext } from '../../../hooks/useAppState';
import { useState, useEffect, use } from 'react';
import FadeTransition from '../../widgetsAndUtils/FadeTransition';

// Hydrology Performance Panel
export default function HydrologyPerformancePanel() {
  const { bmpState, getBmpTypeDisplayName, state, getSelectedBmpLabel } = useAppStateContext();

  // pull a bunch of variables off the global state
  const analysistype = bmpState.analysistype;
  const plot_data = state.hydrology.indexData?.plot_data || [];
  const index_summary = state.hydrology.indexData?.index_summary || [];
  const comparisonMode = state.hydrology.comparisonMode;
  const scatterPlotMargin = state.hydrology.scatterPlotMargin || { top: 30, right: 20, bottom: 40, left: 50 };



  // Single state for comparison gauge visibility
  const [showComparison, setShowComparison] = useState(false);

 
  // Find selected and comparison BMPs
  const selectedBmpData = Array.isArray(index_summary)
    ? index_summary.find(d => typeof d.bmp === 'string' && !d.bmp.startsWith('Other'))
    : null;
  const comparisonBmpData = Array.isArray(index_summary)
    ? index_summary.find(d => typeof d.bmp === 'string' && d.bmp.startsWith('Other'))
    : null;

  // Handle comparison gauge visibility
  useEffect(() => {
    setShowComparison(analysistype === 'individual' && comparisonMode && comparisonBmpData);
  }, [analysistype, comparisonMode, comparisonBmpData]);



  /* 
    Helper functions for the card stack under the performance index plot, displaying the counts of each type of event
    (e.g. Success Excess, Failure, etc.)
  */

  // its going to go through each object in that index_summary data and display the name of the event type and the count
  // This one grabs the name of the event type, since it is a key of the object
  const getSummaryKeys = (data) => {
    if (!data) return [];
    return Object.keys(data).filter(key => 
      key !== 'bmp' && 
      key !== 'performance_index' && 
      key.startsWith('n_')
    );
  };

  // The object keys (as returned from the API) are "n_success", "n_failure", "n_excess" etc
  // We obviously don't want to display that to the user, but rather, "n_success" should render as "Success"
  // Hence why this function exists
  const formatMetricName = (key) => {
    return key.replace('n_', '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // This is for that little section under the performance index plots 
  //   that has those little square thingies that display the counts of each event type (Success, Excess, Fail, etc.)
  const getMetricColor = (key) => {
    const colors = {
      'n_success': 'bg-green-100 text-green-800 border-green-200',
      'n_excess': 'bg-blue-100 text-blue-800 border-blue-200',
      'n_check_data': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'n_failure': 'bg-red-100 text-red-800 border-red-200',
      'n_small_storm_with_bypass': 'bg-purple-100 text-purple-800 border-purple-200',
      'n_events_total': 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return colors[key] || 'bg-gray-100 text-gray-800 border-gray-200';
  };
  /* 
    END of section - Helper functions for the card stack under the performance index plot
  */



  // Only filter for comparison if analysistype is 'individual'
  // I am supposing the component re renders when the analysistype is changed
  //   so I s'pose its no big deal that we do it here rather than in a useEffect hook
  const filteredPlotData = Array.isArray(plot_data)
    ? (analysistype === 'individual'
        ? (comparisonMode ? plot_data : plot_data.filter(d => d.is_comparison !== 'true'))
        : plot_data)
    : plot_data;


  // Helper to render the gauge section with dynamic label
  const renderGaugeSection = (data, label, isComparison = false) => {
    if (!data) return null;
    let displayLabel = label;
    if (analysistype === 'bmptype' && data.bmp) {
      displayLabel = getBmpTypeDisplayName(data.bmp) || data.bmp;
    } else if (isComparison && typeof data.bmp === 'string' && data.bmp.startsWith('Other')) {
      displayLabel = data.bmp;
    } else {
      const site = data.sitename || '';
      const bmp = data.bmpname || data.bmp || '';
      if (site && bmp) displayLabel = `${site} — ${bmp}`;
      else if (bmp) displayLabel = bmp;
      else if (site) displayLabel = site;
    }
    return (
      <div className="mb-6 w-full flex flex-col items-center justify-center">
        <h4 className="text-md font-semibold mb-2 text-center">{displayLabel}</h4>
        <GaugeDial
          value={data.performance_index ?? 0}
          segments={[
            { start: 0, end: 1, color: "#117733" },
            { start: 1, end: 3, color: "#d6cf04" },
            { start: 3, end: 5, color: "#E69F00" },
            { start: 5, end: 10, color: "#d62728" },
          ]}
        />
      </div>
    );
  };


  // Main return
  return (
    <div className="flex flex-col gap-8 my-8 w-full min-h-[440px] py-8">
      {/* Top: Scatter Plot and Gauge (side by side) */}
      <div className="flex flex-col md:flex-row gap-8 w-full">
        {/* Left: Scatter Plot (50%) */}
        <div className="w-full md:w-1/2">
          <HydrologyScatterPlot data={filteredPlotData} />
        </div>
        {/* Right: Gauge (50%) - vertically centered */}
        {/* The Comparison Gauge is wrapped in a <FadeTransition>, so when it mounts and unmounts it fades in and out */}
        <div className="w-full md:w-1/2 flex flex-col items-center justify-center" style={{ paddingBottom: scatterPlotMargin.bottom, minHeight: 400 }}>
          {renderGaugeSection(selectedBmpData, getSelectedBmpLabel(), false)}
          

          <FadeTransition
            in={showComparison}
            timeout={500}
            unmountOnExit
          >
            {renderGaugeSection(comparisonBmpData, "Comparison BMP", true)}
          </FadeTransition>
        </div>
      </div>

      {/* 
        Bottom: Summary Statistics Cards 
        AKA The little card thingies that display the counts of each event type
      */}
      <div className="w-full">
        <h3 className="text-lg font-semibold mb-4 text-gray-800">Performance Summary</h3>
        
        {/* Selected BMP Summary  */}
        <div className="mb-6">
          <h4 className="text-md font-medium mb-3 text-gray-700">{getSelectedBmpLabel()}</h4>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {getSummaryKeys(selectedBmpData).map(key => (
              <div key={key} className={`p-4 rounded-lg border ${getMetricColor(key)}`}>
                <div className="text-2xl font-bold">{selectedBmpData[key]}</div>
                <div className="text-sm opacity-80">{formatMetricName(key)}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Comparison BMP Summary (only show when comparisonMode is true and comparison data exists) */}
        {analysistype === 'individual' && comparisonMode && comparisonBmpData && (
          <div>
            <h4 className="text-md font-medium mb-3 text-gray-700">{analysistype === 'bmptype' && comparisonBmpData?.bmp ? getBmpTypeDisplayName(comparisonBmpData.bmp) || comparisonBmpData.bmp : comparisonBmpData?.bmp || "Other BMPs of the same type"}</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {getSummaryKeys(comparisonBmpData).map(key => (
                <div key={key} className={`p-4 rounded-lg border ${getMetricColor(key)}`}>
                  <div className="text-2xl font-bold">{comparisonBmpData[key]}</div>
                  <div className="text-sm opacity-80">{formatMetricName(key)}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
} 